// ClaudeCode: Login / Register form — with Trust SSO
import { useState } from 'react';

interface LoginFormProps {
  onLogin: (email: string, password: string) => Promise<void>;
  onRegister: (email: string, password: string, displayName: string) => Promise<void>;
  onSsoLogin: () => void;
  ssoError?: string;
}

export function LoginForm({ onLogin, onRegister, onSsoLogin, ssoError }: LoginFormProps) {
  const [mode, setMode] = useState<'login' | 'register'>('login');
  const [showPasswordLogin, setShowPasswordLogin] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [error, setError] = useState(ssoError || '');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      if (mode === 'login') {
        await onLogin(email, password);
      } else {
        await onRegister(email, password, displayName);
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        padding: 24,
      }}
    >
      <div
        style={{
          background: '#18181b',
          border: '1px solid #27272a',
          borderRadius: 12,
          padding: 32,
          width: '100%',
          maxWidth: 400,
        }}
      >
        <h1
          style={{
            textAlign: 'center',
            fontSize: 24,
            fontWeight: 700,
            color: '#fafafa',
            marginBottom: 4,
          }}
        >
          LifeSpace Vault
        </h1>
        <p
          style={{
            textAlign: 'center',
            fontSize: 14,
            color: '#71717a',
            marginBottom: 24,
          }}
        >
          Encrypted secret storage
        </p>

        {/* SSO Login — primary */}
        <button
          onClick={onSsoLogin}
          style={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 10,
            padding: '10px 0',
            background: '#fff',
            color: '#374151',
            border: '1px solid #3f3f46',
            borderRadius: 6,
            fontSize: 15,
            fontWeight: 500,
            cursor: 'pointer',
            marginBottom: 16,
          }}
        >
          <svg width="18" height="18" viewBox="0 0 18 18"><path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 01-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z"/><path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 009 18z"/><path fill="#FBBC05" d="M3.964 10.71A5.41 5.41 0 013.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.997 8.997 0 000 9c0 1.452.348 2.827.957 4.042l3.007-2.332z"/><path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 00.957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z"/></svg>
          Sign in with Google
        </button>

        {error && (
          <div style={{ color: '#f87171', fontSize: 13, marginBottom: 12, textAlign: 'center' }}>
            {error}
          </div>
        )}

        {/* Password login — collapsible fallback */}
        <div style={{ borderTop: '1px solid #27272a', paddingTop: 12 }}>
          <button
            onClick={() => setShowPasswordLogin(!showPasswordLogin)}
            style={{
              width: '100%',
              padding: '6px 0',
              background: 'none',
              color: '#52525b',
              border: 'none',
              fontSize: 13,
              cursor: 'pointer',
            }}
          >
            {showPasswordLogin ? 'Hide' : 'Sign in with email & password'}
          </button>

          {showPasswordLogin && (
            <form onSubmit={handleSubmit} style={{ marginTop: 12 }}>
              {mode === 'register' && (
                <>
                  <label style={{ fontSize: 13, color: '#a1a1aa', display: 'block', marginBottom: 4 }}>
                    Display Name
                  </label>
                  <input
                    type="text"
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    placeholder="Your name"
                    required
                    style={inputStyle}
                  />
                </>
              )}

              <label style={{ fontSize: 13, color: '#a1a1aa', display: 'block', marginBottom: 4 }}>
                Email
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
                autoFocus
                style={inputStyle}
              />

              <label style={{ fontSize: 13, color: '#a1a1aa', display: 'block', marginBottom: 4 }}>
                Password
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Password"
                required
                style={inputStyle}
              />

              <button
                type="submit"
                disabled={loading}
                style={{
                  width: '100%',
                  padding: '10px 0',
                  background: loading ? '#27272a' : '#3b82f6',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 6,
                  fontSize: 15,
                  fontWeight: 600,
                  cursor: loading ? 'wait' : 'pointer',
                  marginBottom: 12,
                }}
              >
                {loading
                  ? 'Please wait...'
                  : mode === 'login'
                  ? 'Sign In'
                  : 'Create Account'}
              </button>

              <button
                onClick={() => {
                  setMode(mode === 'login' ? 'register' : 'login');
                  setError('');
                }}
                type="button"
                style={{
                  width: '100%',
                  padding: '8px 0',
                  background: 'none',
                  color: '#71717a',
                  border: 'none',
                  fontSize: 13,
                  cursor: 'pointer',
                }}
              >
                {mode === 'login'
                  ? "Don't have an account? Register"
                  : 'Already have an account? Sign in'}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '10px 12px',
  background: '#09090b',
  border: '1px solid #27272a',
  borderRadius: 6,
  color: '#fafafa',
  fontSize: 15,
  outline: 'none',
  marginBottom: 16,
};
